import { FormEvent, useCallback, useContext, useEffect, useState } from "react";

import { ProjectStatusConstants } from "../../../../constants";
import { SelectData } from "../../../../models";
import { ProjectContext } from "../../../../route/shared/projects/project/ProjectContext";
import { updateProjectStatus } from "../../../../service/project";
import { lookUp } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useIsLoadingWrapper } from "../../../../utils";
import { Toast } from "../../../../widget";

interface UseUpdateStatusModalReturnData {
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  isStatusOptionsLoading: boolean;
  selectedStatus: string | undefined;
  statusOptions: SelectData;
  setSelectedStatus: (status: string) => void;
  resetForm: (status?: string) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

interface UseUpdateStatusModalProps {
  existingStatus: string;
  closeModal: () => void;
}

export const useUpdateStatusModal = ({
  existingStatus,
  closeModal,
}: UseUpdateStatusModalProps): UseUpdateStatusModalReturnData => {
  const { projectDetails, setProjectDetails } = useContext(ProjectContext);

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);
  const [isStatusOptionsLoading, setIsStatusOptionsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(existingStatus);
  const [statusOptions, setStatusOptions] = useState<SelectData>([]);

  const resetForm = (status?: string): void => {
    setErrors([]);
    setSelectedStatus(status ?? existingStatus);
  };

  const fetchStatusOptions = useCallback(async (): Promise<void> => {
    if (!projectDetails?.standard.uuid) {
      setStatusOptions([]);
      return;
    }

    setIsStatusOptionsLoading(true);

    const response = await lookUp({
      type: ProjectStatusConstants.LOOKUP_TYPE,
      sort: [{ key: "valueString", direction: "asc" }],
    });

    if (response.status === Status.Success) {
      setStatusOptions(
        response.data
          ?.filter((status) => status.key === projectDetails?.standard.uuid)[0]
          .valueString?.split(",")
          .map((item) => item.trim())
          .filter((x) => x !== existingStatus && x !== "")
          .map((x) => ({
            key: x,
            value: x,
          })) ?? []
      );
      setErrors([]);
    }

    if (response.status === Status.Error) {
      setErrors(response.errors);
      setStatusOptions([]);
    }

    setIsStatusOptionsLoading(false);
  }, [existingStatus, projectDetails?.standard.uuid]);

  useEffect(() => {
    fetchStatusOptions().catch((error) => {
      setErrors([
        {
          code: "LOAD_PROJECT_STATUS_OPTIONS_ERROR",
          message: error instanceof Error ? error.message : "Unable to load registry status options",
        },
      ]);
      setIsStatusOptionsLoading(false);
    });
  }, [fetchStatusOptions]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!selectedStatus) {
      setErrors([
        {
          code: "PROJECT_STATUS_REQUIRED",
          message: "Select a registry status",
        },
      ]);
      return;
    }

    if (!projectDetails?.uuid || !projectDetails?.rowVersion) return;

    const res = await updateProjectStatus({
      projectUuid: projectDetails?.uuid,
      rowVersion: projectDetails?.rowVersion,
      status: selectedStatus,
    });

    if (res.status === Status.Success && res.data) {
      Toast.success({ message: "Registry status updated successfully" });

      if (projectDetails) {
        setProjectDetails({
          ...projectDetails,
          status: selectedStatus,
          rowVersion: res.data.rowVersion,
        });
      }

      resetForm(selectedStatus);
      closeModal();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  return {
    errors,
    isHandleSubmitLoading,
    isStatusOptionsLoading,
    selectedStatus,
    statusOptions,
    setSelectedStatus,
    resetForm,
    handleSubmit,
  };
};
